import SVM from "libsvm-js/asm";
import math, { matrix } from "mathjs";
import { ALL_MODELS, DATA_TYPES, MODEL_STATES } from "../constants";
import { argMax } from "../helpers/DataHelpers";
import { stateTypeF } from "../state";
import { BaseModel } from "./BaseModel";
import { dataSplit, infoType } from "./Dataset";

export type svmParamsType = { cost: number; gamma: number; epsilon: number };

export class SupportVectorMachine extends BaseModel {
	modelName = ALL_MODELS.svm;
	lowestLoss = 1e10;
	paramGrid = {
		cost: [],
		gamma: [],
		epsilon: [],
	};

	model: SVM;
	bestParams: svmParamsType;
	modelState = MODEL_STATES.ready;

	svm_type;
	probabilityEstimates: boolean;

	constructor(mlDataInfo: infoType) {
		super(mlDataInfo);
		if (mlDataInfo === undefined) {
			return;
		}

		if (this.mlDataInfo.outputType == DATA_TYPES.number) {
			this.svm_type = SVM.SVM_TYPES.EPSILON_SVR;
			this.probabilityEstimates = false;
			this.paramGrid = {
				cost: [0.1, 1, 10],
				gamma: [1, 0.1, 0.01, 0.001],
				epsilon: [0.1, 0.01, 0.001],
			};
		} else if (this.mlDataInfo.outputType == DATA_TYPES.categorical) {
			this.svm_type = SVM.SVM_TYPES.C_SVC;
			this.probabilityEstimates = true;
			this.paramGrid = {
				cost: [0.1, 1, 10, 100, 1000],
				gamma: [1, 0.1, 0.01, 0.001, 0.0001],
				epsilon: [0],
			};
		}
	}

	initModel(gamma: number, cost: number, epsilon: number): SVM {
		return new SVM({
			kernel: SVM.KERNEL_TYPES.RBF, // The type of kernel I want to use
			type: this.svm_type, // The type of SVM I want to run
			gamma: gamma, // RBF kernel gamma parameter
			cost: cost, // C_SVC cost parameter
			epsilon: epsilon,
			quiet: true,
			tolerance: 0.01,
			probabilityEstimates: this.probabilityEstimates,
			shrinking: false,
			weight: this.mlDataInfo.weights,
		});
	}

	train(trainData: dataSplit, state: stateTypeF, callbackFunction: Function) {
		const nCombinations =
			this.paramGrid.cost.length *
			this.paramGrid.gamma.length *
			this.paramGrid.epsilon.length;
		let completed = 0;
		let bestModel;
		for (let gamma of this.paramGrid.gamma) {
			for (let cost of this.paramGrid.cost) {
				for (let epsilon of this.paramGrid.epsilon) {
					completed += 1;
					this.model = this.initModel(gamma, cost, epsilon);
					this.model.train(trainData.xTrain, trainData.yTrain);
					if (this.isBestModel(trainData.xTest, trainData.yTest)) {
						this.bestParams = {
							cost: cost,
							gamma: gamma,
							epsilon: epsilon,
						};
						bestModel = this.model;
					}
					callbackFunction({
						trainState: "training",
						progress: completed / nCombinations,
					});
				}
			}
		}
		this.model = bestModel;
	}

	predict(X: number[][]) {
		let output = matrix(this.model.predict(X) as number[]);
		let outputAr = output["_data"] as number[];
		return outputAr;
	}

	predictProbs(X: number[][]) {
		let probsObj = this.model.predictProbability(X);
		let predicted = this.model.predict(X);
		let probs: number[][] = [];
		let nLabels: number = probsObj[0].estimates.length;

		let count = 0;
		for (let estimates of probsObj) {
			let probsAr = new Array(nLabels);
			for (let val of estimates.estimates) {
				probsAr[val.label] = val.probability;
			}
			// Rearrange so that the preidcted value has highes probs
			const bestProbIndex = argMax(probsAr);
			const bestProb = probsAr[bestProbIndex];
			probsAr[bestProbIndex] = -100;
			const secondBestProbIndex = argMax(probsAr);
			const secondBestProb = probsAr[secondBestProbIndex];
			const predictedIndex = predicted[count];

			probsAr[bestProbIndex] = secondBestProb;
			probsAr[predictedIndex] = bestProb;

			probs.push(probsAr);
			count += 1;
		}
		return probs;
	}
}
